/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Radio, RadioGroup, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/degree-home';

import type { EnrollableCourse as Course, EnrollableSession as Session } from './types/EnrollableCourse';

const styles = {
  singleSessionViewWrapper: css`
    padding: 1rem;
  `,
  radioGroupCourseName: css`
    padding-bottom: 1rem;
    font-weight: bold;
  `,
};

type SessionRadioGroupProps = {
  course: Course;
  label: string;
  supportText?: string;
  onSelectChange?: (courseId: string, selectedSession: Session) => void;
};

export const SessionRadioGroup = ({ course, label, supportText, onSelectChange }: SessionRadioGroupProps) => {
  return (
    <RadioGroup
      label={label}
      name={_t('#{courseName} session group', { courseName: course.name })}
      supportText={supportText ?? ''}
      onChange={(e) => {
        const sessionId = e.target.value;
        const selectedSession = course.activeAndUpcomingSessions?.elements?.find(
          (session: Session | null) => session?.id === sessionId
        );
        onSelectChange?.(course.id, selectedSession as Session);
      }}
    >
      {course.activeAndUpcomingSessions?.elements.map((session: Session | null) => (
        <Radio
          key={session?.id}
          label={_t('#{startDate} to #{endDate}', {
            startDate: formatDateTimeDisplay(session?.startsAt as number, MED_DATE_ONLY_DISPLAY),
            endDate: formatDateTimeDisplay(session?.endsAt as number, MED_DATE_ONLY_DISPLAY),
          })}
          value={session?.id}
        />
      ))}
    </RadioGroup>
  );
};

type SingleCourseSelectViewProps = {
  course: Course;
  onSelectChange?: (courseId: string, selectedSession: Session) => void;
};

const SingleCourseSelectView = ({ course, onSelectChange }: SingleCourseSelectViewProps) => {
  return (
    <div css={styles.singleSessionViewWrapper}>
      <Typography2 variant="bodyPrimary" component="p" css={styles.radioGroupCourseName}>
        {course.name}
      </Typography2>
      <SessionRadioGroup
        course={course}
        label={_t("Select when you'd like to take this course")}
        onSelectChange={onSelectChange}
      />
    </div>
  );
};

export default SingleCourseSelectView;
